import { useId } from 'react';
import { toast } from 'src/components/ui/toast';
import { T, useGT } from 'gt-react';
import { CopyIcon } from '@phosphor-icons/react';
import {
  taskOutcomeFieldAppliesTo,
  taskOutcomeFieldsForOutcome,
  type TaskOutcomeData,
  type TaskOutcomeField,
} from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from 'src/components/ui/select';

type OutcomeInputProps = {
  field: TaskOutcomeField;
  value: unknown;
  id: string;
  required: boolean;
  disabled: boolean;
  onChange: (value: unknown) => void;
};
function inputText(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
}
function OutcomeSelect({
  id,
  required,
  disabled,
  value,
  options,
  onChange,
}: {
  id: string;
  required: boolean;
  disabled: boolean;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <Select disabled={disabled} value={value} onValueChange={onChange}>
      <SelectTrigger id={id} aria-required={required}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value="unset">
            <T context="unanswered task outcome field">Unanswered</T>
          </SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
function OutcomeInput({
  field,
  value,
  id,
  required,
  disabled,
  onChange,
}: OutcomeInputProps) {
  const gt = useGT();
  const common = { id, required, disabled };
  switch (field.type) {
    case 'boolean':
      return (
        <OutcomeSelect
          {...common}
          value={typeof value === 'boolean' ? String(value) : 'unset'}
          options={[
            {
              value: 'true',
              label: gt('Yes', { context: 'true task outcome field' }),
            },
            {
              value: 'false',
              label: gt('No', { context: 'false task outcome field' }),
            },
          ]}
          onChange={(next) =>
            onChange(next === 'unset' ? null : next === 'true')
          }
        />
      );
    case 'select':
      return (
        <OutcomeSelect
          {...common}
          value={value == null ? 'unset' : `value:${inputText(value)}`}
          options={(field.options ?? []).map((option) => ({
            value: `value:${option}`,
            label: option,
          }))}
          onChange={(next) => onChange(next === 'unset' ? null : next.slice(6))}
        />
      );
    case 'textarea':
      return (
        <Textarea
          {...common}
          aria-required={required}
          value={inputText(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    default:
      return (
        <OutcomeTextInput
          field={field}
          value={value}
          {...common}
          onChange={onChange}
        />
      );
  }
}
function OutcomeTextInput({
  field,
  value,
  id,
  required,
  disabled,
  onChange,
}: OutcomeInputProps) {
  const type =
    field.type === 'number' || field.type === 'date' ? field.type : 'text';
  function change(text: string) {
    if (field.type !== 'number') return onChange(text);
    onChange(text === '' ? null : Number(text));
  }
  return (
    <Input
      id={id}
      type={type}
      value={inputText(value)}
      disabled={disabled}
      aria-required={required}
      onChange={(event) => change(event.target.value)}
    />
  );
}
export function TaskOutcomeFields({
  fields,
  outcome,
  values,
  disabled,
  onChange,
}: {
  fields: readonly TaskOutcomeField[];
  outcome: string;
  values: Record<string, unknown>;
  disabled: boolean;
  onChange: (values: Record<string, unknown>) => void;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-3">
      {taskOutcomeFieldsForOutcome(fields, outcome).map((field) => {
        const inputId = `${id}-${field.key}`;
        const required = field.requiredFor.includes(outcome);
        return (
          <div key={field.key} className="flex flex-col gap-1">
            <Label htmlFor={inputId}>
              {field.label}
              {required ? ' *' : ''}
            </Label>
            <OutcomeInput
              field={field}
              value={values[field.key]}
              id={inputId}
              required={required}
              disabled={disabled}
              onChange={(value) => onChange({ ...values, [field.key]: value })}
            />
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Stored results hold every contract key, with fields scoped to other outcomes
 * as null. Those are not part of this result, so they are hidden rather than
 * shown as "Not provided". Keys with no field definition (legacy data) stay.
 */
function shownOutcomeEntries(
  data: TaskOutcomeData,
  fieldsByKey: ReadonlyMap<string, TaskOutcomeField>,
  outcome: string | null,
): [string, TaskOutcomeData[string]][] {
  return Object.entries(data).filter(([key]) => {
    const field = fieldsByKey.get(key);
    return (
      !field || outcome == null || taskOutcomeFieldAppliesTo(field, outcome)
    );
  });
}

export function TaskOutcomeDataDisplay({
  data,
  fields,
  outcome = null,
}: {
  data: TaskOutcomeData | null;
  fields: readonly TaskOutcomeField[];
  /** The recorded outcome; fields scoped to other outcomes are hidden. */
  outcome?: string | null;
}) {
  const gt = useGT();
  if (!data) return null;
  const fieldsByKey = new Map(fields.map((field) => [field.key, field]));
  return (
    <dl className="flex flex-col gap-1">
      {shownOutcomeEntries(data, fieldsByKey, outcome).map(([key, value]) => (
        <div key={key} className="flex items-center gap-2">
          <dt>{fieldsByKey.get(key)?.label ?? key}</dt>
          <dd>
            {value === null
              ? gt('Not provided', {
                  context: 'absent optional task result value',
                })
              : String(value)}
          </dd>
          {typeof value === 'string' && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label={gt('Copy result', {
                context: 'copy structured task outcome value',
              })}
              onClick={() => {
                void navigator.clipboard.writeText(value).catch(() =>
                  toast.error(
                    gt('Unable to copy result', {
                      context: 'task outcome clipboard error',
                    }),
                  ),
                );
              }}
            >
              <CopyIcon />
            </Button>
          )}
        </div>
      ))}
    </dl>
  );
}
